import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const NextStepsCard = ({ bookingData, onStartCheckIn }) => {
  const nextSteps = [
    {
      icon: 'Clock',
      title: 'Owner Will Contact You',
      description: 'The owner will reach out within 24 hours to confirm pickup details and answer any questions.',
      timeline: 'Within 24 hours',
      status: 'pending'
    },
    {
      icon: 'FileText',
      title: 'Prepare Required Documents',
      description: "Bring a valid driver\'s license and proof of insurance to the pickup location.",
      timeline: 'Before pickup',
      status: 'pending'
    },
    {
      icon: 'ClipboardCheck',
      title: 'Complete Pre-Rental Check-In',
      description: 'Use the guided check-in process to document trailer condition with photos, selfie verification, and digital signature.',
      timeline: 'At pickup',
      status: 'action',
      actionLabel: 'Start Check-In',
      actionIcon: 'Camera'
    },
    {
      icon: 'Truck',
      title: 'Enjoy Your Rental',
      description: 'Use the trailer according to the rental agreement and contact the owner if any issues arise.',
      timeline: 'During rental',
      status: 'pending'
    }
  ];

  const requiredDocuments = [
    { icon: 'CreditCard', label: "Valid Driver\'s License" },
    { icon: 'Shield', label: 'Proof of Insurance' },
    { icon: 'FileText', label: 'Rental Agreement (emailed)' },
    { icon: 'Smartphone', label: 'Booking Confirmation' }
  ];

  const checkInSteps = [
    'Trailer exterior (4 photos)',
    'Interior condition (3 photos)',
    'Hitch & safety chains (2 photos)',
    'Tires & wheels (4 photos)',
    'Electrical systems (2 photos)',
    'Selfie with driver\'s license',
    'Digital signature'
  ];

  return (
    <div className="bg-card rounded-lg shadow-elevation-3 p-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
          <Icon name="ListChecks" size={20} color="var(--color-primary)" strokeWidth={2} />
        </div>
        <h2 className="font-heading text-2xl font-semibold text-foreground">
          Next Steps
        </h2>
      </div>

      {/* Timeline Steps */}
      <div className="space-y-4 mb-6 pb-6 border-b border-border">
        {nextSteps?.map((step, index) => (
          <div key={index} className={`flex items-start space-x-3 ${step?.status === 'action' ? 'p-3 bg-primary/5 border border-primary/20 rounded-lg' : ''}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1 ${
              step?.status === 'action' ? 'bg-primary text-primary-foreground' : 'bg-primary/10'
            }`}>
              <Icon name={step?.icon} size={16} color={step?.status === 'action' ? 'white' : 'var(--color-primary)'} strokeWidth={2} />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between mb-1">
                <h3 className="font-body text-sm font-semibold text-foreground">
                  {step?.title}
                </h3>
                <span className="font-caption text-xs text-muted-foreground">
                  {step?.timeline}
                </span>
              </div>
              <p className="font-body text-xs text-muted-foreground mb-2">
                {step?.description}
              </p>
              {step?.status === 'action' && onStartCheckIn && (
                <Button
                  variant="default"
                  size="sm"
                  iconName={step?.actionIcon}
                  iconPosition="left"
                  onClick={onStartCheckIn}
                  className="w-full mt-1"
                >
                  {step?.actionLabel}
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Required Documents */}
      <div className="mb-6 pb-6 border-b border-border">
        <h3 className="font-heading text-base font-semibold text-foreground mb-3">
          Required Documents
        </h3>
        <div className="space-y-2">
          {requiredDocuments?.map((doc, index) => (
            <div key={index} className="flex items-center space-x-3">
              <Icon name={doc?.icon} size={16} color="var(--color-primary)" strokeWidth={2} />
              <span className="font-body text-sm text-foreground">{doc?.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Check-In Checklist */}
      <div>
        <h3 className="font-heading text-base font-semibold text-foreground mb-3">
          Check-In Checklist
        </h3>
        <div className="space-y-2">
          {checkInSteps?.map((item, index) => (
            <div key={index} className="flex items-start space-x-2">
              <Icon name="Square" size={14} color="var(--color-muted-foreground)" strokeWidth={2} className="mt-0.5" />
              <span className="font-body text-sm text-muted-foreground">{item}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Important Notice */}
      <div className="mt-6 pt-6 border-t border-border">
        <div className="flex items-start space-x-3 p-3 bg-warning/10 rounded-md">
          <Icon name="AlertTriangle" size={18} color="var(--color-warning)" strokeWidth={2} />
          <div>
            <p className="font-body text-sm font-medium text-foreground mb-1">
              Important Notice
            </p>
            <p className="font-body text-xs text-muted-foreground">
              Complete the check-in process before departing with the trailer. This protects you from liability for pre-existing damage.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NextStepsCard;